// src/features/records/components/MapView/hooks/useViewportRecords.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchRecordsByIds } from '../../../services/core/queries';
import { mapBoundsToBoundingBoxes, queryEdgesInBounds } from '../../../services/edges/geo';
import { searchEdges } from '../../../services/edges/search';
import useEntityCache from '../../../stores/entityCache';
import useUser from '../../../../../hooks/useUser';
import { logError, ErrorSeverity, ErrorCategory } from '../../../../../services/errorService';

const BOUNDS_DELTA_EPSILON = 0.001;

function timestampToMillis(value) {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value === 'object' && value !== null && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

/**
 * useViewportRecords - Edge-Indexed viewport query hook
 *
 * Queries edges within map bounds and hydrates entities on demand.
 * Part of the Edge-Indexed Lists architecture migration.
 *
 * @param {Object} bounds - Map viewport bounds
 * @param {Object} [options] - Viewport query options
 * @returns {Object} Records in viewport with loading state
 */
const useViewportRecords = (bounds, options = {}) => {
  const { user } = useUser();
  const uid = user?.uid;

  const {
    searchTerm,
    scope,
    perRangeLimit = 50,
    debounceMs = 300,
    maxPins = 300,
    prefetchCount = 20,
    ownerIds
  } = options;

  const trimmedSearchTerm = typeof searchTerm === 'string' ? searchTerm.trim() : '';
  const isSearching = trimmedSearchTerm.length > 0;

  const derivedOwnerIds = useMemo(() => {
    if (Array.isArray(ownerIds) && ownerIds.length > 0) {
      return ownerIds.slice(0, 10);
    }
    if (scope?.mode === 'users' && Array.isArray(scope.userIds) && scope.userIds.length > 0) {
      return scope.userIds.slice(0, 10);
    }
    if (Array.isArray(scope?.ownerIds) && scope.ownerIds.length > 0) {
      return scope.ownerIds.slice(0, 10);
    }
    return null;
  }, [ownerIds, scope]);

  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchCursor, setSearchCursor] = useState(null);
  const loadingRef = useRef(false);
  const lastBoundsRef = useRef(null);
  const inFlightRequestRef = useRef(0);
  const lastSearchSignatureRef = useRef(null);
  const searchModeRef = useRef(isSearching);

  const effectiveMaxPins = Number.isFinite(maxPins) && maxPins > 0 ? maxPins : null;

  const scopeSignature = useMemo(() => {
    if (!scope) return '';

    if (scope.mode === 'users') {
      const ids = Array.isArray(scope.userIds) ? [...scope.userIds] : [];
      ids.sort();
      return JSON.stringify({ mode: 'users', userIds: ids });
    }

    if (scope.mode === 'group') {
      return JSON.stringify({ mode: 'group', groupId: scope.groupId ?? null });
    }

    return JSON.stringify(scope);
  }, [scope]);

  const hasBoundsChanged = useCallback((prev, next) => {
    if (!prev || !next) return Boolean(next);

    return (
      Math.abs(next.north - prev.north) > BOUNDS_DELTA_EPSILON ||
      Math.abs(next.south - prev.south) > BOUNDS_DELTA_EPSILON ||
      Math.abs(next.east - prev.east) > BOUNDS_DELTA_EPSILON ||
      Math.abs(next.west - prev.west) > BOUNDS_DELTA_EPSILON
    );
  }, []);

  useEffect(() => {
    if (!uid || !bounds) {
      inFlightRequestRef.current += 1;
      loadingRef.current = false;
      lastBoundsRef.current = null;
      lastSearchSignatureRef.current = null;
      setPins([]);
      setSearchCursor(null);
      setError(null);
      setLoading(false);
    }
  }, [uid, bounds]);

  useEffect(() => {
    if (searchModeRef.current === isSearching) return;

    searchModeRef.current = isSearching;
    inFlightRequestRef.current += 1;
    loadingRef.current = false;
    lastBoundsRef.current = null;
    lastSearchSignatureRef.current = null;
    setPins([]);
    setSearchCursor(null);
    setError(null);
    setLoading(false);
  }, [isSearching]);

  const byId = useEntityCache(s => s.byId);
  const getOrFetchMany = useEntityCache(s => s.getOrFetchMany);

  const buildPreviewRecord = useCallback((pin) => {
    const preview = pin.preview ?? {};

    // Map view only needs location + title previews; keep entity hydration optional.
    const location = {
      lat: pin.lat,
      lng: pin.lng
    };
    if (pin.geohash) {
      location.geohash = pin.geohash;
    }
    if (preview.title) {
      location.name = preview.title;
    }

    return {
      id: pin.id,
      attributes: {
        location
      },
      content: {
        title: preview.title ?? '',
        body: preview.body ?? '',
        tags: preview.tags ?? []
      },
      metadata: {
        updatedAt: pin.query?.recordUpdatedAt ?? pin.updatedAt ?? null
      },
      access: {
        owner: pin.ownerId ?? null
      },
      _isPreview: true
    };
  }, []);

  const isCacheEntryFresh = useCallback((entry, pin) => {
    if (!entry?.entity) return false;

    const edgeUpdatedAt = timestampToMillis(pin.updatedAt ?? pin.query?.recordUpdatedAt);
    const entityUpdatedAt = timestampToMillis(entry.entity?.metadata?.updatedAt);

    if (edgeUpdatedAt && entityUpdatedAt) {
      return entityUpdatedAt >= edgeUpdatedAt;
    }

    return true;
  }, []);

  useEffect(() => {
    if (!uid || !bounds || isSearching) return;

    const prevBounds = lastBoundsRef.current;
    if (!hasBoundsChanged(prevBounds, bounds)) return;

    const requestId = ++inFlightRequestRef.current;
    lastBoundsRef.current = { ...bounds };
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    setSearchCursor(null);

    const timeoutId = setTimeout(async () => {
      try {
        const edgePins = await queryEdgesInBounds({
          uid,
          bounds,
          perRangeLimit,
          ownerIds: derivedOwnerIds
        });

        if (inFlightRequestRef.current !== requestId) return;

        const sorted = edgePins.slice().sort((a, b) => {
          const aTs = timestampToMillis(a.query?.recordUpdatedAt);
          const bTs = timestampToMillis(b.query?.recordUpdatedAt);

          if (aTs !== bTs) {
            return (bTs ?? 0) - (aTs ?? 0);
          }

          if (a.id === b.id) return 0;
          return a.id < b.id ? 1 : -1;
        });

        const limited = effectiveMaxPins ? sorted.slice(0, effectiveMaxPins) : sorted;

        setPins(limited.map(pin => ({
          id: pin.id,
          lat: pin.lat,
          lng: pin.lng,
          geohash: pin.geohash,
          preview: pin.preview ?? {},
          query: pin.query ?? {},
          ownerId: pin.ownerId ?? null,
          updatedAt: pin.updatedAt ?? pin.query?.recordUpdatedAt ?? null
        })));
      } catch (err) {
        if (inFlightRequestRef.current !== requestId) return;

        console.error('Error loading viewport records:', err);

        logError(err, {
          severity: ErrorSeverity.WARNING,
          category: ErrorCategory.DATA_LOADING,
          component: 'useViewportRecords',
          action: 'loadRecords',
          metadata: bounds ? {
            boundsArea: (bounds.north - bounds.south) * (bounds.east - bounds.west)
          } : undefined
        });

        setPins([]);
        setError(err || { type: 'MAP_VIEWPORT_LOAD_FAILED' });
      } finally {
        if (inFlightRequestRef.current === requestId) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    uid,
    bounds,
    perRangeLimit,
    derivedOwnerIds,
    effectiveMaxPins,
    debounceMs,
    isSearching,
    hasBoundsChanged
  ]);

  useEffect(() => {
    if (!uid || !bounds || !isSearching) return;

    const geoBoundingBoxes = mapBoundsToBoundingBoxes(bounds);
    const geoSignature = geoBoundingBoxes?.map((box) => box.join(',')) ?? null;
    const searchSignature = JSON.stringify({
      term: trimmedSearchTerm,
      scope: scopeSignature,
      geo: geoSignature
    });

    const prevSignature = lastSearchSignatureRef.current;
    const prevBounds = lastBoundsRef.current;

    if (!hasBoundsChanged(prevBounds, bounds) && prevSignature === searchSignature) {
      return;
    }

    const requestId = ++inFlightRequestRef.current;
    lastBoundsRef.current = { ...bounds };
    lastSearchSignatureRef.current = searchSignature;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    setPins([]);
    setSearchCursor(null);

    const timeoutId = setTimeout(async () => {
      try {
        const pageSize = Math.max(1, Math.min(effectiveMaxPins ?? 50, 50));
        const geo = geoBoundingBoxes && geoBoundingBoxes.length ? { insideBoundingBox: geoBoundingBoxes } : undefined;

        const result = await searchEdges({
          uid,
          viewType: 'map',
          scope,
          searchTerm: trimmedSearchTerm,
          pageSize,
          cursor: null,
          geo
        });

        if (inFlightRequestRef.current !== requestId) return;

        const seen = new Set();
        const mappedPins = (result.items || [])
          .map(item => {
            const edge = item.edge ?? {};
            const preview = edge.preview ?? item.preview ?? {};
            const loc = edge.query?.loc ?? {};
            const algoliaLoc = edge._geoloc ?? item.edge?._geoloc ?? item._geoloc ?? {};

            const lat = Number.isFinite(loc.lat) ? loc.lat : Number.isFinite(algoliaLoc.lat) ? algoliaLoc.lat : null;
            const lng = Number.isFinite(loc.lng) ? loc.lng : Number.isFinite(algoliaLoc.lng) ? algoliaLoc.lng : null;

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              return null;
            }

            if (seen.has(item.id)) {
              return null;
            }
            seen.add(item.id);

            return {
              id: item.id,
              lat,
              lng,
              geohash: loc.geohash ?? null,
              preview,
              query: edge.query ?? {},
              ownerId: edge.ownerId ?? null,
              updatedAt: edge.updatedAt ?? edge.query?.recordUpdatedAt ?? null
            };
          })
          .filter(Boolean);

        const limitedPins = effectiveMaxPins ? mappedPins.slice(0, effectiveMaxPins) : mappedPins;

        setPins(limitedPins);
        setSearchCursor(result.nextCursor ?? null);
      } catch (err) {
        if (inFlightRequestRef.current !== requestId) return;

        console.error('Error searching viewport records:', err);

        logError(err, {
          severity: ErrorSeverity.WARNING,
          category: ErrorCategory.DATA_LOADING,
          component: 'useViewportRecords',
          action: 'searchRecords',
          metadata: {
            searchTerm: trimmedSearchTerm,
            scope: scopeSignature ? JSON.parse(scopeSignature) : null
          }
        });

        setPins([]);
        setError(err || { type: 'MAP_VIEWPORT_SEARCH_FAILED' });
      } finally {
        if (inFlightRequestRef.current === requestId) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    uid,
    bounds,
    scope,
    scopeSignature,
    trimmedSearchTerm,
    effectiveMaxPins,
    debounceMs,
    isSearching,
    hasBoundsChanged
  ]);

  useEffect(() => {
    if (!pins.length || prefetchCount <= 0) return;

    const now = Date.now();

    const missingIds = pins
      .slice(0, prefetchCount)
      .filter(pin => {
        const entry = byId.get(pin.id);
        if (!entry) return true;
        if (entry.expiresAt <= now) return true;
        return !isCacheEntryFresh(entry, pin);
      })
      .map(pin => pin.id);

    if (!missingIds.length) return;

    // Hydrate a small front-of-queue window so detail views open without delay.
    getOrFetchMany(missingIds, fetchRecordsByIds).catch(err => {
      console.error('Entity hydration failed:', err);
    });
  }, [pins, byId, getOrFetchMany, prefetchCount, isCacheEntryFresh]);

  const { records, hydratedCount } = useMemo(() => {
    const now = Date.now();
    let hydrated = 0;

    const derived = pins.map(pin => {
      const entry = byId.get(pin.id);
      if (entry && entry.expiresAt > now && isCacheEntryFresh(entry, pin)) {
        hydrated += 1;
        return entry.entity;
      }

      return buildPreviewRecord(pin);
    });

    return { records: derived, hydratedCount: hydrated };
  }, [pins, byId, buildPreviewRecord, isCacheEntryFresh]);

  const hydrate = useCallback(async (ids) => {
    const normalized = Array.isArray(ids) ? ids : [ids];
    if (!normalized.length) return Array.isArray(ids) ? [] : null;

    const entities = await getOrFetchMany(normalized, fetchRecordsByIds);
    if (Array.isArray(ids)) {
      return entities;
    }

    return entities.find(entity => entity?.id === normalized[0]) ?? null;
  }, [getOrFetchMany]);

  return {
    records,
    loading,
    error,
    hydrate,
    searchCursor,
    pins,
    totalPins: pins.length,
    hydrated: hydratedCount
  };
};

export default useViewportRecords;
